import Dialog from "@components/Dialog"
import { Button, Typography } from "@mui/material"
import { useAtom } from "jotai"
import { createEdgesDialogAtom } from "@features/builder/CreateEdges/createEdgesDialogAtom"
import { addEdgeToNode } from "@features/builder/helpers/add-edge"
import { Edge, useEdges, useNodes, useReactFlow } from "@xyflow/react"
import { getMaybeConnections } from "./helpers"
import { AppNode, EdgeData, NodeType } from "@features/builder/graph/types"
import { useGetLinkedNode } from "@features/builder/hooks/useGetLinkedNode"
import { useSnackbar } from "notistack"
import { useState } from "react"
import CreateEdgesForm from "./CreateEdgesForm"
import CreateEdgesPreview from "./CreateEdgesPreview"
import type { CreateEdgesDialogViewMode, MaybeConnection } from "./types"
import * as yup from "yup"
import { createEdgesValidationSchema } from "@features/builder/CreateEdges/validation"
import { sortNodesAlphabetically } from "@utils/node/sortNodesAlphabetically"

type FormValues = yup.InferType<typeof createEdgesValidationSchema>

export default function CreateEdgesDialog() {
  const { enqueueSnackbar } = useSnackbar()

  const [{ open, sourceNodeIds: sourceNodes, initialDirection }, setCreateEdgesDialog] =
    useAtom(createEdgesDialogAtom)

  const [viewMode, setViewMode] = useState<CreateEdgesDialogViewMode>("Form")

  const { updateNode, getNodes } = useReactFlow<AppNode, Edge<EdgeData>>()
  const { getLinkedNode } = useGetLinkedNode()
  const nodes = useNodes<AppNode>()
  const edges = useEdges<Edge<EdgeData>>()

  const [maybeConnections, setMaybeConnections] = useState<MaybeConnection[]>([])

  const handleClose = () => {
    setViewMode("Form")
    setMaybeConnections([])
    setCreateEdgesDialog({
      open: false,
      sourceNodeIds: [],
      initialDirection: "Parents",
    })
  }

  const handleBack = () => setViewMode("Form")

  const onPreview = (values: FormValues) => {
    setViewMode("Preview")
    setMaybeConnections(
      getMaybeConnections({
        parents: values.direction === "Parents" ? values.selectedNodes : values.targetNodes,
        children: values.direction === "Parents" ? values.targetNodes : values.selectedNodes,
        allNodes: nodes,
        allEdges: edges,
      }),
    )
  }

  const handleApply = async () => {
    const originalNodesMap = new Map(getNodes().map((node) => [node.id, node]))
    const updatedNodesMap = new Map(originalNodesMap)

    const selectedConnections = maybeConnections.filter(({ isSelected }) => isSelected)

    const resolvedLinkedNodes = await Promise.all(
      selectedConnections.map(async ({ parent }) => {
        const linkedNodeId = "metaNode" in parent.data ? parent.data.metaNode?.Link : undefined
        const linkedNode = await getLinkedNode(linkedNodeId)
        return { linkedNodeId, linkedNode }
      }),
    )

    for (let index = 0; index < selectedConnections.length; index++) {
      const selectedConnection = selectedConnections[index]
      const resolvedLinkedNode = resolvedLinkedNodes[index]
      if (!selectedConnection || !resolvedLinkedNode) {
        continue
      }

      const { parent, child } = selectedConnection
      const { linkedNodeId, linkedNode } = resolvedLinkedNode

      if (linkedNodeId && !linkedNode) {
        enqueueSnackbar(`Could not create connection: linked node ${linkedNodeId} not found`, {
          variant: "error",
        })
        continue
      }

      const currentParent = updatedNodesMap.get(parent.id) ?? parent
      const currentChild = updatedNodesMap.get(child.id) ?? child

      const updatedNodes = addEdgeToNode(
        currentParent,
        currentChild,
        [...updatedNodesMap.values()],
        linkedNode,
      )
      updatedNodes.forEach((updatedNode) => {
        updatedNodesMap.set(updatedNode.id, updatedNode)
      })
    }

    updatedNodesMap.forEach((updatedNode, id) => {
      if (updatedNode !== originalNodesMap.get(id)) {
        updateNode(id, updatedNode, { replace: true })
      }
    })

    handleClose()
  }

  // there is at least one connection selected
  const isApplyDisabled = !maybeConnections.some(({ isSelected }) => isSelected)

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Create Edges"
      closeOnBackdropClick
      fullWidth
      maxWidth="sm"
      fullScreen={false}
      actionsComponent={
        viewMode === "Preview" ? (
          <>
            <Button variant="outlined" onClick={handleBack}>
              Back
            </Button>
            <Button disabled={isApplyDisabled} variant="contained" onClick={handleApply}>
              Apply
            </Button>
          </>
        ) : (
          <>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" form="create-edges-form" variant="contained">
              Preview
            </Button>
          </>
        )
      }
    >
      <Typography color="text.secondary" sx={{ mb: 2 }}>
        Create edges between selected nodes and target nodes.
      </Typography>
      {open && viewMode === "Form" ? (
        <CreateEdgesForm
          initialSourceNodes={nodes.filter((node) => sourceNodes.includes(node.id))}
          initialDirection={initialDirection}
          allNodes={nodes
            .filter((node) => node.type !== NodeType.Group)
            .sort(sortNodesAlphabetically)}
          onPreview={onPreview}
        />
      ) : (
        <CreateEdgesPreview
          maybeConnections={maybeConnections}
          setMaybeConnections={setMaybeConnections}
        />
      )}
    </Dialog>
  )
}
